import { useState } from 'react'
import ChannelItem from './ChannelItem'
import ListRow from '../ListRow'
import Text from '../Text'
import type { PlaylistChannel } from '@expostreamer/shared-types'
import styles from './style'

interface ChannelListProps {
  searchResults: PlaylistChannel[]
  accessToken: string | null
  onChannelItemClick?: (url: string) => void
}

type ListEntry =
  | { kind: 'channel'; item: PlaylistChannel }
  | { kind: 'season'; key: string; seriesName: string; season: number; episodes: PlaylistChannel[] }

// Series episodes are bucketed by series + season (in order of first
// appearance) so episodes from different seasons don't blur together in the
// search results; everything else is rendered as a plain row.
function groupResults(searchResults: PlaylistChannel[]): ListEntry[] {
  const entries: ListEntry[] = []
  const seasons = new Map<string, Extract<ListEntry, { kind: 'season' }>>()

  for (const item of searchResults) {
    if (!item.series) {
      entries.push({ kind: 'channel', item })
      continue
    }

    const key = item.series.name + '\u0000' + item.series.season
    let entry = seasons.get(key)
    if (!entry) {
      entry = {
        kind: 'season',
        key,
        seriesName: item.series.name,
        season: item.series.season,
        episodes: [],
      }
      seasons.set(key, entry)
      entries.push(entry)
    }
    entry.episodes.push(item)
  }

  for (const entry of seasons.values()) {
    entry.episodes.sort((a, b) => (a.series?.episode ?? 0) - (b.series?.episode ?? 0))
  }

  return entries
}

function episodeLabel(item: PlaylistChannel): string | undefined {
  const name = item.name ?? ''
  const seriesName = item.series?.name ?? ''
  return name.startsWith(seriesName) ? name.slice(seriesName.length).trim() || name : name
}

function ChannelList({
  searchResults,
  accessToken,
  onChannelItemClick = undefined,
}: ChannelListProps) {
  const [collapsedSeasons, setCollapsedSeasons] = useState<Set<string>>(new Set())

  function toggleSeason(key: string) {
    setCollapsedSeasons(previous => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div style={styles.container}>
      {groupResults(searchResults).map(entry => {
        if (entry.kind === 'channel') {
          return (
            <ChannelItem
              key={entry.item.url}
              item={entry.item}
              onChannelItemClick={onChannelItemClick}
            />
          )
        }

        const isCollapsed = collapsedSeasons.has(entry.key)
        const title = `${entry.seriesName} · Staffel ${entry.season}`

        return (
          <div key={entry.key}>
            <ListRow
              leading={<Text style={styles.chevron}>{isCollapsed ? '▸' : '▾'}</Text>}
              label={`${title} (${entry.episodes.length})`}
              tooltip={title}
              labelColor='#4caf50'
              onClick={() => toggleSeason(entry.key)}
            />
            {isCollapsed ? null : (
              <div style={styles.seasonEpisodes}>
                {entry.episodes.map(item => (
                  <ChannelItem
                    key={item.url}
                    item={item}
                    label={episodeLabel(item)}
                    onChannelItemClick={onChannelItemClick}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ChannelList
